"use client";

import { motion } from "framer-motion";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { useRef, useState } from "react";
import type { YouTubePlaylistItem } from "@/lib/youtube";

interface VideoCardProps {
  video: YouTubePlaylistItem;
  onPlay: (videoId: string, title: string) => void;
  index: number;
}

const VideoCard = ({ video, onPlay, index }: VideoCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
      className="group relative flex-shrink-0 w-[280px] md:w-[320px] cursor-pointer"
      onClick={() => onPlay(video.videoId, video.title)}
    >
      <div className="relative aspect-video rounded-xl overflow-hidden bg-zinc-200 dark:bg-zinc-900 ring-1 ring-black/5 dark:ring-white/5 group-hover:ring-brand-red/50 transition-all duration-300 group-hover:scale-105 group-hover:z-10 group-hover:shadow-2xl group-hover:shadow-brand-red/20">
        {video.thumbnail ? (
          <Image
            src={video.thumbnail}
            alt={video.title}
            fill
            sizes="(max-width: 768px) 280px, 320px"
            className="object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-red/20 to-zinc-300 dark:to-zinc-900 flex items-center justify-center">
            <Play className="w-12 h-12 text-black/30 dark:text-white/30" />
          </div>
        )}

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />

        {/* Play Button */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
          <div className="w-14 h-14 rounded-full bg-brand-red flex items-center justify-center scale-75 group-hover:scale-100 transition-transform shadow-xl">
            <Play className="w-7 h-7 text-white fill-current ml-1" />
          </div>
        </div>

        {/* Duration badge (placeholder) */}
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/80 text-[10px] font-bold text-white opacity-0 group-hover:opacity-100 transition-opacity">
          WATCH
        </div>
      </div>

      {/* Title */}
      <div className="mt-3 pr-2">
        <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 group-hover:text-black dark:group-hover:text-white line-clamp-2 transition-colors">
          {video.title}
        </h3>
      </div>
    </motion.div>
  );
};

interface VideoRowTranslations {
  viewAll?: string;
  all?: string;
  new?: string;
  officialChannel?: string;
  watch?: string;
}

const defaultRowTranslations: VideoRowTranslations = {
  viewAll: "VIEW ALL",
  all: "ALL",
  new: "New",
  officialChannel: "Official Channel",
  watch: "WATCH",
};

interface VideoRowProps {
  title: string;
  videos: YouTubePlaylistItem[];
  onPlayVideo: (videoId: string, title: string) => void;
  playlistId?: string;
  hideHeader?: boolean;
  translations?: VideoRowTranslations;
}

export const VideoRow = ({ title, videos, onPlayVideo, playlistId, hideHeader, translations = defaultRowTranslations }: VideoRowProps) => {
  const t = { ...defaultRowTranslations, ...translations };
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const scrollAmount = 340 * 3;
    const newPosition =
      scrollRef.current.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount);
    scrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
  };

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  if (!videos || videos.length === 0) return null;

  // Check if this is a special section
  const isRecentlyAdded = title.toLowerCase().includes("recently");
  const isMainChannel = title.toLowerCase().includes("youth alive");

  return (
    <div className="relative group/row py-6 md:py-8">
      {/* Row Header */}
      {!hideHeader && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex items-end justify-between mb-6 px-6 md:px-12"
        >
          <div className="flex items-center gap-3 md:gap-4">
            {/* Accent line */}
            <div className="hidden md:block w-1 h-8 bg-gradient-to-b from-brand-red to-brand-red/0 rounded-full" />

            <div>
              {/* Category label for special sections */}
              {(isRecentlyAdded || isMainChannel) && (
                <div className="flex items-center gap-2 mb-1">
                  {isRecentlyAdded && (
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-red">
                      {t.new}
                    </span>
                  )}
                  {isMainChannel && (
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-red">
                      {t.officialChannel}
                    </span>
                  )}
                </div>
              )}

              {/* Main title */}
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-black text-zinc-900 dark:text-white tracking-tight uppercase">
                {title}
              </h2>
            </div>
          </div>

          {playlistId && (
            <a
              href={`https://www.youtube.com/playlist?list=${playlistId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group/link flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-300 dark:border-white/10 text-xs font-bold text-zinc-600 dark:text-zinc-400 hover:text-brand-red hover:border-brand-red dark:hover:text-brand-red dark:hover:border-brand-red transition-all"
            >
              <span className="hidden sm:inline">{t.viewAll}</span>
              <span className="sm:hidden">{t.all}</span>
              <ChevronRight className="w-4 h-4 group-hover/link:translate-x-0.5 transition-transform" />
            </a>
          )}
        </motion.div>
      )}

      {/* Scroll Container */}
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={() => scroll("left")}
          aria-label={`Scroll ${title} videos left`}
          className={`absolute left-0 top-0 bottom-0 z-20 w-12 md:w-16 bg-gradient-to-r from-zinc-100 via-zinc-100/80 dark:from-brand-black dark:via-brand-black/80 to-transparent flex items-center justify-start pl-2 transition-opacity duration-300 ${
            showLeftArrow ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 flex items-center justify-center transition-colors">
            <ChevronLeft className="w-6 h-6 text-zinc-700 dark:text-white" />
          </div>
        </button>

        {/* Videos */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto scrollbar-hide px-6 md:px-12 pb-4"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {videos.map((video, index) => (
            <VideoCard
              key={video.id}
              video={video}
              onPlay={onPlayVideo}
              index={index}
            />
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll("right")}
          aria-label={`Scroll ${title} videos right`}
          className={`absolute right-0 top-0 bottom-0 z-20 w-12 md:w-16 bg-gradient-to-l from-zinc-100 via-zinc-100/80 dark:from-brand-black dark:via-brand-black/80 to-transparent flex items-center justify-end pr-2 transition-opacity duration-300 ${
            showRightArrow ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-black/10 dark:bg-white/10 hover:bg-black/20 dark:hover:bg-white/20 flex items-center justify-center transition-colors">
            <ChevronRight className="w-6 h-6 text-zinc-700 dark:text-white" />
          </div>
        </button>
      </div>
    </div>
  );
};
